import type { Dict } from '@/types/dict'
import { EFormFieldErrorLevel, type FormFieldValidatorFunc } from './field'
import { isDigit, isString } from '@/utils/is'
import { parseHTMLToString } from '@/utils/html'

const isNone = (x: any): x is undefined | null | '' =>
  typeof x === 'undefined' || x === null || x === ''

type ValidatorMap<T extends Dict> = {
  [K in keyof T]: (...args: any[]) => FormFieldValidatorFunc
}

const createMap = <T extends Dict, K = ValidatorMap<T>>(cfg: K): K => {
  return cfg
}

export const Validators = createMap({
  required: () => {
    return (_field, value) => {
      if (isNone(value) || (Array.isArray(value) && value.length <= 0) || parseHTMLToString(value) === "") {
        return { message: 'Field required', level: EFormFieldErrorLevel.REQUIRED }
      }
      return null
    }
  },
  min: (min: number) => {
    return (_field, value) => {
      const message = (() => {
        if (typeof value === 'string') return `Must be at least ${min} characters.`
        if (typeof value === 'number') return `Must not be less than ${min}`
        return `Must be at least ${min}`
      })()
      if (isNone(value)) return { message }
      if (typeof value === 'string' && value.length < min) return { message }
      if (typeof value === 'number' && value < min) return { message }
      return null
    }
  },
  max: (max: number) => {
    return (_field, value) => {
      const message = (() => {
        if (typeof value === 'string') return `Must not contain more than ${max} characters.`
        if (typeof value === 'number') return `Must not be greater than ${max}`
        return `Must be not be more than ${max}`
      })()
      if (isNone(value)) return null
      if (typeof value === 'string' && value.length > max) return { message }
      if (typeof value === 'number' && value > max) return { message }
      return null
    }
  },
  range: (min: number, max: number, errorMessage?: string) => {
    return (_field, value) => {
      const message = errorMessage || `Must be between ${min} and ${max}`
      if (value < min || value > max) return { message }
      return null
    }
  },
  personalNumber: () => {
    return (_field, value) => {
      if (!value) return undefined
      if (!isString(value)) return { message: 'Invalid value' }
      const digits = Array.from(value).filter((c) => isDigit(c))
      if (digits.length !== 12) return { message: 'Must be 12 digits' }
      const now = new Date()
      const currentYear = now.getFullYear()
      const year = Number(digits.slice(0, 4).join(''))
      if (!(year >= 1960 && year <= currentYear)) return { message: `Invalid year: ${year}` }
      const month = Number(digits.slice(4, 4 + 2).join(''))
      if (!(month >= 1 && month <= 12)) return { message: `Invalid month: ${month}` }
      const day = Number(digits.slice(4 + 2, 4 + 2 + 2).join(''))
      if (!(day >= 1 && day <= 31)) return { message: `Invalid day: ${day}` }
    }
  },
  email: () => {
    return (_field, value) => {
      if (!isString(value)) return { message: 'Invalid value' }
      if (value.length <= 0) return undefined
      if (
        !String(value)
          .toLowerCase()
          .match(
            /^[a-z0-9]+([._+-][a-z0-9]+)*@[a-z0-9]+([.-][a-z0-9]+)*\.[a-z]{2,}$/
          )
      )
        return { message: 'Invalid email' }
    }
  }
})

//export const FormFieldRequired = (): DataFormFieldValidatorFunction => {
//  return (_field, value) => {
//    if (typeof value === 'undefined' || value === null || value === '') {
//      return { message: 'Field required' }
//    }
//    return null;
//  }
//}
